import React, { useState, useMemo, useEffect } from "react";
import FloatingLabelInput from "../../UIElements/floatingbuttoninput";
import PaymentOption from "../../UIElements/PaymentOptions";
import FormButtonStyle4 from "../../UIElements/FormButton";
import Header from "../Header/header";
import callApi from "../../../../utility/apiCaller";
import { useDispatch, useSelector } from "react-redux";
import useLeadInitializer from "../../Utils/setStoreData";

const selectedIcon = "/assets/img/selected.png";
const unselectedIcon = "/assets/img/unselected.png";

const getTenures = (minTenure, maxTenure) => {
  const step = 6;
  const tenures = [];
  for (let t = minTenure; t <= maxTenure; t += step) tenures.push(t);
  if (tenures[tenures.length - 1] !== maxTenure) tenures.push(maxTenure);
  return tenures;
};

const calculateEMI = (principal, annualInterest, months) => {
  const P = Number(principal);
  console.log(principal, annualInterest);
  const r = annualInterest / 12 / 100;
  const n = months;
  const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  return Math.ceil(emi * 100) / 100;
};

export default function CustomizeLoan({ setCurrentStep, selectedOffer }) {
  const aaSlice = useSelector((state) => state?.aa);
  const offersData = useSelector((state) => state.app.offers);
  const user = useSelector((state) => state.app.user);

  const amountReq = String(aaSlice?.amountReq ?? "2000000");
  const [amount, setAmount] = useState(amountReq);
  const [selectedPlan, setSelectedPlan] = useState({});
  const [errors, setErrors] = useState({});
  const [timer, setTimer] = useState(null);
  const [roi,setRoi] = useState(null)

  const [tenure, setTenure] = useState({
    min_tenure: 6,
    max_tenure: selectedOffer?.tenure || 36,
  });

  const leadId = aaSlice?.internalLeadId;

  const dispatch = useDispatch()
  const initLead = useLeadInitializer(dispatch)



  const allOffers = useMemo(() => {
    return Array.isArray(offersData)
      ? offersData.flatMap((item) => item.OfferDetails || [])
      : offersData?.OfferDetails
      ? offersData.OfferDetails
      : [];
  }, [offersData]);

  console.log(allOffers);

    useEffect(() => {
  

    if (leadId) {
      initLead(leadId);  // 🔥 ONE LINE AND EVERYTHING GETS SET
    }
  }, []);

  // Convert amount string to number
  const principal = useMemo(
    () => parseInt(amount?.replace(/[^0-9]/g, "")) || 0,
    [amount]
  );

  const handleAmountChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = parseInt(rawValue) || 0;

    const minAmount = 50000;
    const maxAmount = Number(amountReq) || selectedOffer?.amount || Infinity;

    if (numericValue > maxAmount) {
      setAmount(String(maxAmount));
      setErrors((prev) => {
        const { amount, ...rest } = prev;
        return rest;
      });
      return;
    }

    setAmount(rawValue);

    if (timer) clearTimeout(timer);
    const newTimer = setTimeout(() => {
      if (numericValue < minAmount) {
        setErrors((prev) => ({
          ...prev,
          amount: `Amount must be at least ₹ ${minAmount}`,
        }));
      } else if (numericValue % 5000 !== 0) {
        setErrors((prev) => ({
          ...prev,
          amount:
            "Please enter an amount in multiples of 5,000 (for example: 55,000 or 1,05,000)",
        }));
      } else {
        setErrors((prev) => {
          const { amount, ...rest } = prev;
          return rest;
        });
      }
    }, 100);
    setTimer(newTimer);
  };

  // 🔥 Effect: Run both on mount and whenever amount changes
  useEffect(() => {
    const IntVal = parseInt(amount?.replace(/[^0-9]/g, "")) || 0;
    if (!allOffers?.length) return;

    const matchedOffer = allOffers.find(
      (data) => IntVal >= data.min_amount && IntVal <= data.max_amount
    );

    if (matchedOffer) {
      setRoi(matchedOffer.roi)
      setTenure((prev) => {
        if (
          prev.min_tenure !== matchedOffer.min_tenor ||
          prev.max_tenure !== matchedOffer.max_tenor
        ) {
          return {
            min_tenure: matchedOffer.min_tenor,
            max_tenure: matchedOffer.max_tenor,
          };
        }
        return prev;
      });
    } else {
      // fallback 🔥
      setTenure({
        min_tenure: 6,
        max_tenure: selectedOffer?.tenure || 36,
      });
    }
  }, [amount, allOffers, selectedOffer]);

  // 🔥 Clear selected plan if tenure range changes
  useEffect(() => {
    if (
      selectedPlan.months &&
      (selectedPlan.months < tenure.min_tenure ||
        selectedPlan.months > tenure.max_tenure)
    ) {
      setSelectedPlan({});
    }
  }, [tenure, selectedPlan]);

  // 🔥 Recalculate EMI whenever tenure or interest changes
  const emiPlans = useMemo(() => {
    const tenures = getTenures(tenure.min_tenure, tenure.max_tenure);
   
    return tenures.map((months) => ({
      months,
      emi: calculateEMI(principal, selectedOffer?.interest || roi, months),
    }));
  }, [principal, tenure, selectedOffer]);

  const handleClickNext = async () => {
    const numericValue = principal;
    const minAmount = 50000;
    const maxAmount = Number(amountReq) || selectedOffer?.amount || Infinity;

    if (numericValue < minAmount) {
      setErrors((prev) => ({
        ...prev,
        amount: `Amount must be at least ₹ ${minAmount}`,
      }));
      return;
    } else if (numericValue > maxAmount) {
      setErrors((prev) => ({
        ...prev,
        amount: `Amount cannot exceed ₹ ${maxAmount}`,
      }));
      return;
    } else if (numericValue % 5000 !== 0) {
      setErrors((prev) => ({
        ...prev,
        amount:
          "Please enter an amount in multiples of 5,000 (for example: 55,000 or 1,05,000)",
      }));
      return;
    }

    try {
      const payload = {
        lead: {
          dmi_offer: {
            tenure: String(selectedPlan.months),
            interest: String(roi),
            amount: String(amount),
            emi: String(selectedPlan.emi),
          },
        },
      };

      await callApi(
        `v1/lead/${leadId}/update-lead-new`,
        "post",
        payload,
        "core",
        user?.token ||
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2OTAxYjljZjIxZTZlNTJmYTQwNDAwNDkiLCJvdHAiOiIxMTExIiwiY29udGFjdF9waG9uZSI6Ijk2OTMxMzU0NjYiLCJvdHBfZXhwaXJ5IjoiMjAyNS0xMC0yOVQwNzowMzowMy43MDZaIiwiaWF0IjoxNzYxNzIwNzg3fQ.xJ3Qd29aQPzV6vfc-bm5XDaOyIF1dXI4dD3lRZxEBDo"
      );

      await callApi(
        `v1/lead/${leadId}/update-lead-new`,
        "post",
        {
          lead: {
            is_customise_loan_completed: true,
            steps_completed: 9,
          },
        },
        "core",
        user?.token ||
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2OTAxYjljZjIxZTZlNTJmYTQwNDAwNDkiLCJvdHAiOiIxMTExIiwiY29udGFjdF9waG9uZSI6Ijk2OTMxMzU0NjYiLCJvdHBfZXhwaXJ5IjoiMjAyNS0xMC0yOVQwNzowMzowMy43MDZaIiwiaWF0IjoxNzYxNzIwNzg3fQ.xJ3Qd29aQPzV6vfc-bm5XDaOyIF1dXI4dD3lRZxEBDo"
      );

      setCurrentStep((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  return (
    <div className="max-w-[400px] min-h-[100dvh] sm:min-h-content w-screen pb-4 overflow-y-scroll mx-auto font-atkinson flex flex-col">
      <Header />
      <div className="space-y-4 flex-1 mt-2 px-3">
        <h2 className="text-[20px] font-bold">Customize Your Loan</h2>
        <div>
          <div className="text-[10px] text-right m-0 pt-1 pr-0.5 text-yellow-600">
            <p>
              *Your maximum limit is ₹{" "}
              {Number(amountReq).toLocaleString("en-IN")}
            </p>
          </div>
          <FloatingLabelInput
            label="Edit your Loan Amount"
            value={`₹ ${principal.toLocaleString("en-IN")}`}
            onChange={handleAmountChange}
            readOnly={false}
            errorMessage={errors.amount}
            maxLength={String(selectedOffer?.amount || "").length}
          />
        </div>

        <div className="ml-5">
          <p className="mt-4 flex justify-left items-center gap-1">
            Choose your EMI Plan{" "}
            <img
              src="/assets/img/Layer_1 (42).png"
              className="h-[14px] w-[14px]"
            />
          </p>
          <p className="text-[12px] text-[#B3B3B3]">
            Interest @{roi}% per year.
          </p>
        </div>

        {!errors.amount &&
          emiPlans.map((plan) => (
            <PaymentOption
              key={plan.months}
              amount={plan.emi}
              months={plan.months}
              recommended={plan.recommended}
              selected={selectedPlan?.months === plan.months}
              selectedIcon={selectedIcon}
              unselectedIcon={unselectedIcon}
              onSelect={() => setSelectedPlan(plan)}
            />
          ))}
      </div>

      <div className="h-6" />

      <FormButtonStyle4
        text="Next"
        onClick={handleClickNext}
        disabled={
          !selectedPlan.months ||
          Object.keys(errors).length > 0 ||
          principal < 50000
        }
        className="mt-auto sm:mt-[200px]"
      />
    </div>
  );
}
